import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { AddToCalendarButton } from 'add-to-calendar-button-react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import dayjs from 'dayjs';
import {
    Button,
  Calendar,
  Modal,
  Select,
  Spin,
  Table,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarked } from '@fortawesome/pro-duotone-svg-icons';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1 } from '../../shared-components/typography/Title';
import api from '../../../api/api';
import { notificationsAtom, viewLeadAtom, reloadLeadsAtom } from '../../../atoms/Atoms';
import { mapAppointmentStatus, toFormattedLocalDate, toFormattedLocalHour, toLocalTime } from '../../../functions';
import { appointmentFilters } from '../../../utils';

function MyAppointments() {
  const { Option } = Select;
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState({
    start: dayjs().startOf('month'),
    end: dayjs().endOf('month')
  });
  const [viewLead, setViewLead] = useRecoilState(viewLeadAtom);
  const [reloadLeads, setReloadLeads] = useRecoilState(reloadLeadsAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [selectedDate, setSelectedDate] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAppointment, setLoadingAppointment] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(3);
  const [selectedDateAppointments, setSelectedDateAppointments] = useState([]);

  function isWithinRange(date) {
    const isAfterOrSameAsStart = dateRange.start.isSame(date) || dateRange.start.isAfter(date);
    const isAfterOrSameAsEnd = dateRange.end.isSame(date) || dateRange.end.isBefore(date);
    return isAfterOrSameAsStart && isAfterOrSameAsEnd;
  }

  function getDayAppointments(date) {
    const startDate = date.startOf('day');
    const endDate = date.endOf('day');
    const dayAppointments = _.filter(appointments, (a) => (dayjs(a.appointmentDate).isSame(startDate) || dayjs(a.appointmentDate).isAfter(startDate)) && (dayjs(a.appointmentDate).isSame(startDate) || dayjs(a.appointmentDate).isBefore(endDate)));
    setSelectedDateAppointments(dayAppointments);
  }

  function getData(start, end, dateToSelect) {
    setDateRange({
      start,
      end
    });
    const startString = start.format('YYYY-MM-DDTHH:mm:ss');
    const endString = end.format('YYYY-MM-DDTHH:mm:ss');
    setLoading(true);
    api.get(`leads/me/appointments?startDate=${startString}&endDate=${endString}&filter=${selectedFilter}`)
    .then((response) => {
      setLoading(false);
      if (response.data.success) {
        setAppointments(response.data.data);
        // if (dateToSelect) {
        //   const startDate = dateToSelect.startOf('day');
        //   const endDate = dateToSelect.endOf('day');
        //   const dayAppointments = _.filter(appointments, (a) => dayjs(a.appointmentDate) >= startDate && dayjs(a.appointmentDate) <= endDate);
        //   if (dayAppointments.length > 0) {
        //     setTimeout(() => {
        //       getDayAppointments(dateToSelect);
        //       setSelectedDate(dateToSelect);
        //     }, 500);
        //   }
        // }
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response?.data?.error,
            error: true,
          },
        ]);
      }
    })
    .catch((error) => {
      setLoading(false);
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error?.message,
          error: true,
        },
      ]);
    });
  }

  function getAppointment(id) {
    setLoadingAppointment(true);
    api.get(`leads/lead/${id}`)
    .then((response) => {
      setLoadingAppointment(false);
      if (response.data.success) {
        setViewLead(response.data.data);
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response?.data?.error,
            error: true,
          },
        ]);
      }
    })
    .catch((error) => {
      setLoadingAppointment(false);
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error?.message,
          error: true,
        },
      ]);
    });
  }

  useEffect(() => {
    if (reloadLeads) {
      setReloadLeads(false);
      getData(dateRange.start, dateRange.end);
    }
  }, [reloadLeads]);

  useEffect(() => {
    if (!viewLead) {
      getData(dateRange.start, dateRange.end, selectedDate);
    }
  }, [viewLead]);

  useEffect(() => {
    getData(dateRange.start, dateRange.end);
  }, [selectedFilter]);

  useEffect(() => {
    getData(dateRange.start, dateRange.end);
  }, []);

  useEffect(() => {
    if (selectedDate) {
      getDayAppointments(selectedDate);
    } else {
      setSelectedDateAppointments([]);
    }
  }, [selectedDate]);

  return (
    <div>
      <div className="md:flex my-4 items-center space-y-2">
        {/* <H1 text={t('my_appointments')} /> */}
        <Select
          className="w-full md:w-48 ml-auto"
          value={selectedFilter}
          disabled={loading}
          onChange={(obj) => setSelectedFilter(obj)}
        >
          {_.map(appointmentFilters, (f) => (
            <Option value={f.id}>{t(f.name)}</Option>
          ))}
        </Select>
        <Button
          type="primary"
          size="small"
          loading={loading}
          className="md:ml-2 w-full md:w-auto"
          onClick={() => getData(dateRange.start, dateRange.end)}
        >
          {t('reload')}
        </Button>
      </div>
      <Spin spinning={loading}>
        <Calendar
          mode="month"
          showWeek={false}
          loading
          yearControlWidth="0"
          monthControlWidth="0"
          onPanelChange={(value, mode) => {
            setDateRange({
              start: value.startOf('month'),
              end: value.endOf('month')
            });
          }}
          onSelect={(date, selectInfo) => {
            if (selectInfo.source === 'date') {
              setSelectedDate(date);
            } else {
              getData(date.startOf('month'), date.endOf('month'), date);
            }
          }}
          cellRender={(date, info) => {
            const startDate = date.startOf('day');
            const endDate = date.endOf('day');
            const dayAppointments = _.filter(appointments, (a) => (dayjs(a.appointmentDate).isSame(startDate) || dayjs(a.appointmentDate).isAfter(startDate)) && (dayjs(a.appointmentDate).isSame(startDate) || dayjs(a.appointmentDate).isBefore(endDate)));
            const isPast = dayjs() > date;
            return (
              <>
                <div className="hidden md:block tight-line-height">
                  {dayAppointments?.length > 1 && (
                    <div className={`rounded-full text-center text-xs items-center h-4 w-4 ${isPast ? 'bg-gray-400 text-white' : 'bg-green-400 text-white'}`}>
                      {dayAppointments?.length}
                    </div>
                  )}
                  {_.map(dayAppointments, (a) => (
                    <div className={`${isPast ? 'text-gray-400' : ''} border my-2 text-center justify-center`}>
                      <p>{a.leadSubmission.submittedBy.completeName}</p>
                      <p className="mt-1">{toFormattedLocalHour(a.appointmentDate)}</p>
                      {(selectedFilter === 0 || selectedFilter === 3) && (
                        <div className="italic text-xs">
                          {t(mapAppointmentStatus(a.appointmentStatus))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {dayAppointments?.length > 0 && (
                  <div className="md:hidden text-center items-center justify-items-center">
                    <div className={`rounded-full text-center items-center h-6 w-6 ${isPast ? 'bg-gray-400 text-white' : 'bg-green-400 text-white'}`}>
                      {dayAppointments?.length}
                    </div>
                  </div>
                )}
              </>
            );
          }}
        />
      </Spin>
      <Modal
        open={selectedDate}
        footer={null}
        zIndex={997}
        onCancel={() => setSelectedDate(null)}
      >
        <strong>{toFormattedLocalDate(selectedDate)}</strong>
        <Table
          className="mt-2 w-full"
          loading={loading}
          dataSource={selectedDateAppointments}
          bordered
          size="small"
          columns={[
            {
              title: '',
              align: 'center',
              className: 'text-sm w-full',
              render: (row) => (
                <div>
                  <div className="items-center justify-center text-center mb-4">
                    <AddToCalendarButton
                      trigger="click"
                      name={`${row.leadSubmission.submittedBy} - ${row.leadSubmission.municipality}`}
                      options={['Outlook.com', 'Apple', 'Google', 'Yahoo', 'iCal']}
                      startDate={toLocalTime(row.appointmentDate).format('YYYY-MM-DD')}
                      startTime={toLocalTime(row.appointmentDate).format('HH:mm')}
                      endTime={toLocalTime(row.appointmentDate).add(1, 'hour').format('HH:mm')}
                      timeZone="America/Puerto_Rico"
                      location={`${row.leadSubmission.addressLine1} ${row.leadSubmission.addressLine2}`}
                    />
                  </div>
                  <p>{row.leadSubmission.submittedBy.completeName}</p>
                  <p>{toFormattedLocalHour(row.appointmentDate)}</p>
                  {row.leadSubmission.externalDealId && (
                    <p>{row.leadSubmission.externalDealId}</p>
                  )}
                  {(selectedFilter === 0 || selectedFilter === 3) && (
                    <div className="italic text-xs">
                      {t(mapAppointmentStatus(row.appointmentStatus))}
                    </div>
                  )}
                  {(row.leadSubmission.addressLine1 || row.leadSubmission.addressLine2) && (
                    <div className="mt-4">
                      {row.leadSubmission.addressLine1 && (
                        <p>{row.leadSubmission.addressLine1}</p>
                      )}
                      {row.leadSubmission.addressLine2 && (
                        <p>{row.leadSubmission.addressLine2}</p>
                      )}
                    </div>
                  )}
                  {row.leadSubmission.location?.lat !== 0 && row.leadSubmission.location?.lon !== 0 && (
                    <div>
                      <a
                        href={`https://www.google.com/maps?q=${row.leadSubmission.location.lat},${row.leadSubmission.location.lon}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 text-2xl"
                        aria-label={`View location on map for ${row.leadSubmission?.municipality ?? 'this place'}`}
                      >
                        <FontAwesomeIcon icon={faMapMarked} className="text-primaryColor mr-2" />
                      </a>
                    </div>
                  )}
                  <Button
                    type="primary"
                    className="mt-6 w-full md:w-2/3"
                    loading={loadingAppointment}
                    onClick={() => getAppointment(row.leadSubmission.id)}
                  >
                    {t('view')}
                  </Button>
                </div>
              )
            }
          ]}
        />
      </Modal>
    </div>
  );
}

export default MyAppointments;
